(() => {
  const ROOT_SELECTOR = '[data-tvati-get-started]';
  const DIALOG_SELECTOR = '[data-tvati-get-started-dialog]';
  const TRIGGER_SELECTOR =
    'a[href="#getstarted"], [data-tvati-get-started-trigger], [data-tvati-open-get-started]';

  const PRODUCT_SELECTION_PREFIX = 'tvati:product-selection:';
  const GET_STARTED_HANDOFF_PREFIX = 'tvati:get-started:selection:';
  const RECOMMENDATION_STORAGE_KEY = 'tvati:get-started:recommendation';

  const safeParse = (value, fallback = {}) => {
    try {
      return JSON.parse(value || '');
    } catch (error) {
      return fallback;
    }
  };

  const readSession = (key, fallback = {}) => {
    try {
      return safeParse(sessionStorage.getItem(key), fallback);
    } catch (error) {
      return fallback;
    }
  };

  const writeSession = (key, value) => {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {}
  };

  const removeSession = (key) => {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {}
  };

  const normalizePath = (value) => {
    if (!value) return '';

    try {
      const url = new URL(value, window.location.origin);
      const path = url.pathname || '/';

      return path.length > 1 ? path.replace(/\/+$/, '') : path;
    } catch (error) {
      const path = String(value).split('?')[0].split('#')[0] || '/';
      return path.length > 1 ? path.replace(/\/+$/, '') : path;
    }
  };

  const getRoot = () => document.querySelector(ROOT_SELECTOR);

  const isPetContext = (root) => {
    const configured = normalizePath(root?.dataset.petPagePath || '/pages/tvati-pet');
    const current = normalizePath(window.location.pathname);

    return Boolean(configured && current === configured);
  };

  const isPetProductPage = (root, recommendation = null) => {
    const current = normalizePath(window.location.pathname);

    const recommendationPath = normalizePath(recommendation?.url || '');
    const productPath = normalizePath(root?.dataset.petProductUrl || '');

    if (recommendationPath && current === recommendationPath) return true;
    if (productPath && current === productPath) return true;

    return false;
  };

  const getDialog = (root) => root?.querySelector(DIALOG_SELECTOR);

  const getStep = (root, name) =>
    root?.querySelector(`[data-tvati-get-started-step="${name}"]`) || null;

  const showStep = (root, name) => {
    if (!root) return;

    root.querySelectorAll('[data-tvati-get-started-step]').forEach((step) => {
      const active = step.dataset.tvatiGetStartedStep === name;
      step.hidden = !active;
      step.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    root.dataset.tvatiGetStartedActiveStep = name;
  };

  const stripGetStartedHash = () => {
    if (window.location.hash !== '#getstarted') return;

    history.replaceState(
      null,
      '',
      `${window.location.pathname}${window.location.search}`
    );
  };

  const closeModal = (root, { stripHash = true } = {}) => {
    const dialog = getDialog(root);

    if (dialog?.open) {
      dialog.close();
    }

    document.documentElement.classList.remove('tvati-get-started-open');

    if (stripHash) {
      stripGetStartedHash();
    }
  };

  const openModal = (root) => {
    const dialog = getDialog(root);
    if (!root || !dialog) return;

    root.dataset.tvatiGetStartedReturnStep = isPetContext(root)
      ? 'pet'
      : 'generic';

    root.dataset.tvatiGetStartedAudience = '';

    showStep(root, root.dataset.tvatiGetStartedReturnStep);

    if (!dialog.open) {
      dialog.showModal();
    }

    document.documentElement.classList.add('tvati-get-started-open');

    if (window.location.hash !== '#getstarted') {
      history.replaceState(
        null,
        '',
        `${window.location.pathname}${window.location.search}#getstarted`
      );
    }

    requestAnimationFrame(() => {
      root.querySelector('[data-tvati-get-started-close]')?.focus({
        preventScroll: true
      });
    });
  };

  const openSizeStep = (root, audience) => {
    if (!root) return;

    const animal = audience === 'cat' ? 'CAT' : 'DOG';

    root.dataset.tvatiGetStartedAudience = audience || 'dog';

    const animalNode = root.querySelector('[data-tvati-get-started-animal]');
    if (animalNode) animalNode.textContent = animal;

    showStep(root, 'size');

    requestAnimationFrame(() => {
      root.querySelector('[data-tvati-get-started-size]')?.focus({
        preventScroll: true
      });
    });
  };

  const findMatchingProductSizeButton = (productId, sizeIndex) => {
    const purchases = [...document.querySelectorAll('[data-tvati-purchase]')];

    const purchase = purchases.find(
      (node) => String(node.dataset.productId || '') === String(productId || '')
    );

    if (!purchase) return null;

    return (
      [...purchase.querySelectorAll('[data-tvati-size-option]')].find(
        (button) => Number(button.dataset.sizeIndex) === Number(sizeIndex)
      ) || null
    );
  };

  const buildSelectionPayload = (root, button) => {
    const productId = String(root.dataset.petProductId || '');
    const sizeIndex = Number(button.dataset.sizeIndex) || 0;

    return {
      productId,
      sizeIndex,
      sizeLabel: button.dataset.sizeLabel || '',
      sizeRange: button.dataset.sizeRange || '',
      minVialsPerWeek: Number(button.dataset.minVialsWeek) || 0,
      maxVialsPerWeek:
        Number(button.dataset.maxVialsWeek) ||
        Number(button.dataset.minVialsWeek) ||
        0,
      variantId: String(button.dataset.variantId || ''),
      vialCount: Number(button.dataset.vialCount) || 0,
      sellingPlanId: '',
      purchaseMode: 'one-time',
      breed: '',
      audience: root.dataset.tvatiGetStartedAudience || 'dog'
    };
  };

  const publishSelection = (payload) => {
    if (!payload?.productId) return;

    writeSession(
      `${PRODUCT_SELECTION_PREFIX}${payload.productId}`,
      payload
    );

    window.TvatiProductSelection = payload;
    window.TvatiProductSelections = window.TvatiProductSelections || {};
    window.TvatiProductSelections[payload.productId] = payload;

    document.dispatchEvent(
      new CustomEvent('tvati:product-selection-change', {
        detail: payload
      })
    );
  };

  const recommendationText = (button) => {
    const productTitle = String(button.dataset.productTitle || 'Tvati Pet').trim();
    const packLabel = String(button.dataset.packLabel || '').trim();
    const vialCount = Number(button.dataset.vialCount) || 0;
    const sizeLabel = String(button.dataset.sizeLabel || '').trim();

    let recommendation = '';

    if (packLabel) {
      recommendation = `${productTitle} · ${packLabel}`;
    } else if (vialCount) {
      recommendation =
        `${productTitle} · ${vialCount} ${vialCount === 1 ? 'VIAL' : 'VIALS'}`;
    } else if (sizeLabel) {
      recommendation = `${productTitle} · ${sizeLabel}`;
    } else {
      recommendation = productTitle;
    }

    return recommendation;
  };

  const catRecommendationText = (button) => {
    const productTitle = String(button.dataset.productTitle || 'Tvati Pet').trim();
    const packLabel = String(button.dataset.packLabel || '12 PACK').trim();
    const vialCount = Number(button.dataset.vialCount) || 12;

    if (packLabel) {
      return `${productTitle} · ${packLabel}`;
    }

    return `${productTitle} · ${vialCount} ${vialCount === 1 ? 'VIAL' : 'VIALS'}`;
  };

  const selectCatRecommendation = (root, button) => {
    if (!root || !button) return;

    const destination =
      button.dataset.tvatiGetStartedUrl ||
      root.dataset.petProductUrl ||
      '';

    const productId = String(root.dataset.petProductId || '');
    const variantId = String(button.dataset.variantId || '');
    const vialCount = Number(button.dataset.vialCount) || 12;

    /*
     * Cat skips the dog size question, but still uses Tvati Pet 12 as the
     * recommendation. Persist enough state for Product Main to land on the
     * same recommended variant.
     */
    if (productId) {
      const payload = {
        productId,
        sizeIndex: 0,
        sizeLabel: 'CAT',
        sizeRange: '',
        minVialsPerWeek: 0,
        maxVialsPerWeek: 0,
        variantId,
        vialCount,
        sellingPlanId: '',
        purchaseMode: 'one-time',
        breed: '',
        audience: 'cat'
      };

      publishSelection(payload);
    }

    if (root.dataset.showRecommendation === 'true') {
      const recommendation = {
        text: catRecommendationText(button),
        url: destination || root.dataset.petProductUrl || '#',
        productId,
        variantId,
        sizeIndex: 0,
        sizeLabel: 'CAT',
        pendingDestination: true,
        createdAt: Date.now()
      };

      writeSession(RECOMMENDATION_STORAGE_KEY, recommendation);
    }

    closeModal(root, { stripHash: false });

    if (destination) {
      window.location.assign(destination);
    }
  };

  const hideRecommendation = (root, { clear = false } = {}) => {
    const pill = root?.querySelector('[data-tvati-get-started-pill]');

    if (root?._tvatiRecommendationTimer) {
      window.clearTimeout(root._tvatiRecommendationTimer);
      root._tvatiRecommendationTimer = null;
    }

    if (pill) pill.hidden = true;

    if (clear) {
      removeSession(RECOMMENDATION_STORAGE_KEY);
    }
  };

  const showRecommendation = (root, recommendation) => {
    if (!root || root.dataset.showRecommendation !== 'true') return;

    const pill = root.querySelector('[data-tvati-get-started-pill]');
    const text = root.querySelector('[data-tvati-get-started-pill-text]');
    const prefix = root.querySelector('[data-tvati-get-started-pill-prefix]');

    if (!pill || !text || !recommendation?.text) return;

    if (root._tvatiRecommendationTimer) {
      window.clearTimeout(root._tvatiRecommendationTimer);
      root._tvatiRecommendationTimer = null;
    }

    text.textContent = recommendation.text;

    if (prefix) {
      prefix.textContent =
        root.dataset.recommendationPrefix ||
        'WE RECOMMEND';
    }

    pill.hidden = false;

    if (root.dataset.recommendationAutoDismiss === 'true') {
      const seconds =
        Math.max(3, Number(root.dataset.recommendationDismissSeconds) || 6);

      root._tvatiRecommendationTimer = window.setTimeout(() => {
        hideRecommendation(root, { clear: true });
      }, seconds * 1000);
    }
  };

  const storeRecommendation = (root, button, { showNow = false } = {}) => {
    if (!root || root.dataset.showRecommendation !== 'true') return null;

    const recommendation = {
      text: recommendationText(button),
      url:
        button.dataset.productUrl ||
        root.dataset.petProductUrl ||
        '#',
      productId: root.dataset.petProductId || '',
      variantId: button.dataset.variantId || '',
      sizeIndex: Number(button.dataset.sizeIndex) || 0,
      sizeLabel: button.dataset.sizeLabel || '',
      pendingDestination: true,
      createdAt: Date.now()
    };

    writeSession(RECOMMENDATION_STORAGE_KEY, recommendation);

    if (showNow) {
      showRecommendation(root, recommendation);
    }

    return recommendation;
  };

  const restoreRecommendation = (root) => {
    if (!root || root.dataset.showRecommendation !== 'true') return;

    const recommendation = readSession(RECOMMENDATION_STORAGE_KEY, {});

    if (!recommendation?.text) return;

    /*
     * This is intentionally one-shot:
     * - never show it on random pages;
     * - show it only when we actually arrive on the Tvati Pet PDP;
     * - consume the session value immediately once shown, so navigating away
     *   cannot make it follow the shopper around the site.
     *
     * The visible pill stays in the DOM until its timer or X dismisses it.
     */
    if (!isPetProductPage(root, recommendation)) {
      return;
    }

    showRecommendation(root, recommendation);
    removeSession(RECOMMENDATION_STORAGE_KEY);
  };

  const selectPetSize = (root, button) => {
    if (!root || !button) return;

    const payload = buildSelectionPayload(root, button);
    const destination =
      button.dataset.productUrl ||
      root.dataset.petProductUrl ||
      '';

    /*
     * Always persist the choice first so Product Main can hydrate the exact
     * size state when the shopper arrives on the PDP.
     */
    publishSelection(payload);

    /*
     * If this exact product builder is already present on the current page,
     * use its native size button and show the recommendation immediately.
     * Otherwise store it silently, navigate, then restore the pill on the
     * destination product page.
     */
    const productSizeButton = findMatchingProductSizeButton(
      payload.productId,
      payload.sizeIndex
    );

    if (productSizeButton) {
      productSizeButton.click();
      storeRecommendation(root, button, { showNow: true });
      closeModal(root);
      return;
    }

    /*
     * One-shot handoff to Product Main.
     *
     * Medium and Large can legitimately recommend the same pack/variant, so
     * the variant URL alone cannot reconstruct the shopper's actual size
     * answer after navigation.
     */
    if (payload.productId && destination) {
      writeSession(
        `${GET_STARTED_HANDOFF_PREFIX}${payload.productId}`,
        {
          ...payload,
          pendingDestination: true,
          createdAt: Date.now()
        }
      );
    }

    storeRecommendation(root, button, { showNow: false });

    if (destination) {
      closeModal(root, { stripHash: false });
      window.location.assign(destination);
      return;
    }

    closeModal(root);
  };

  const navigateAudience = (root, button) => {
    const url = button.dataset.tvatiGetStartedUrl || '';

    if (!url || button.getAttribute('aria-disabled') === 'true') return;

    hideRecommendation(root, { clear: true });
    closeModal(root, { stripHash: false });

    window.location.href = url;
  };

  const initRoot = (root) => {
    if (!root || root.dataset.tvatiGetStartedReady === 'true') return;
    root.dataset.tvatiGetStartedReady = 'true';

    const dialog = getDialog(root);

    if (!dialog) return;

    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('tvati-get-started-open');
    });

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeModal(root);
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) {
        closeModal(root);
        return;
      }

      const close = event.target.closest('[data-tvati-get-started-close]');
      if (close) {
        closeModal(root);
        return;
      }

      const back = event.target.closest('[data-tvati-get-started-back]');
      if (back) {
        showStep(
          root,
          root.dataset.tvatiGetStartedReturnStep || 'generic'
        );
        return;
      }

      const size = event.target.closest('[data-tvati-get-started-size]');
      if (size) {
        selectPetSize(root, size);
        return;
      }

      const audience = event.target.closest('[data-tvati-get-started-audience]');
      if (!audience) return;

      const audienceType = audience.dataset.tvatiGetStartedAudience || '';

      if (audience.hasAttribute('data-tvati-get-started-cat-recommendation')) {
        selectCatRecommendation(root, audience);
        return;
      }

      if (audience.dataset.tvatiGetStartedNext === 'size') {
        openSizeStep(root, audienceType);
        return;
      }

      navigateAudience(root, audience);
    });

    root
      .querySelector('[data-tvati-get-started-pill-close]')
      ?.addEventListener('click', () => {
        hideRecommendation(root, { clear: true });
      });

    restoreRecommendation(root);
  };

  const initAll = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  };

  initAll();

  if (!window.__tvatiGetStartedGlobalReady) {
    window.__tvatiGetStartedGlobalReady = true;

    document.addEventListener(
      'click',
      (event) => {
        const trigger = event.target.closest(TRIGGER_SELECTOR);
        if (!trigger) return;

        const root = getRoot();
        if (!root) return;

        event.preventDefault();
        event.stopPropagation();

        initRoot(root);
        openModal(root);
      },
      true
    );

    window.addEventListener('hashchange', () => {
      const root = getRoot();
      const dialog = getDialog(root);

      if (!root || !dialog) return;

      if (window.location.hash === '#getstarted') {
        initRoot(root);
        openModal(root);
      } else if (dialog.open) {
        dialog.close();
        document.documentElement.classList.remove('tvati-get-started-open');
      }
    });

    document.addEventListener('shopify:section:load', (event) => {
      const root = event.target.matches?.(ROOT_SELECTOR)
        ? event.target
        : event.target.querySelector?.(ROOT_SELECTOR);

      if (root) {
        initRoot(root);
        restoreRecommendation(root);
      }
    });
  }

  if (window.location.hash === '#getstarted') {
    requestAnimationFrame(() => {
      const root = getRoot();
      if (!root) return;

      initRoot(root);
      openModal(root);
    });
  }
})();
